<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-4">Dashboard Administrateur</h1>
        <p class="text-subtitle-1 text-grey mb-6">
          Gestion et configuration du système de recrutement
        </p>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Utilisateurs totaux</p>
                <h2 class="text-h4">{{ stats.totalUsers }}</h2>
              </div>
              <v-avatar color="primary" size="48">
                <v-icon size="24">mdi-account-multiple</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Postes actifs</p>
                <h2 class="text-h4">{{ stats.activeJobs }}</h2>
              </div>
              <v-avatar color="success" size="48">
                <v-icon size="24">mdi-briefcase</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Candidatures</p>
                <h2 class="text-h4">{{ stats.totalApplications }}</h2>
              </div>
              <v-avatar color="info" size="48">
                <v-icon size="24">mdi-file-document</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Système</p>
                <h2 class="text-h5 text-success">En ligne</h2>
              </div>
              <v-avatar color="success" size="48">
                <v-icon size="24">mdi-server</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card>
          <v-card-title>Actions rapides</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-btn
                  block
                  color="primary"
                  prepend-icon="mdi-robot"
                  @click="$router.push('/admin/llm-settings')"
                >
                  Configuration IA
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  block
                  color="info"
                  prepend-icon="mdi-database"
                  @click="$router.push('/admin/database-settings')"
                >
                  Base de données
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  block
                  color="warning"
                  prepend-icon="mdi-message"
                  @click="$router.push('/admin/channels-settings')"
                >
                  Canaux de communication
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  block
                  color="success"
                  prepend-icon="mdi-backup-restore"
                  @click="backupSystem"
                >
                  Sauvegarde
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- System Status -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>État du système</v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="success">mdi-check-circle</v-icon>
                </template>
                <v-list-item-title>Serveur Web</v-list-item-title>
                <v-list-item-subtitle>Opérationnel</v-list-item-subtitle>
              </v-list-item>
              
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="success">mdi-check-circle</v-icon>
                </template>
                <v-list-item-title>Base de données</v-list-item-title>
                <v-list-item-subtitle>Connectée</v-list-item-subtitle>
              </v-list-item>
              
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="warning">mdi-alert</v-icon>
                </template>
                <v-list-item-title>Service IA</v-list-item-title>
                <v-list-item-subtitle>Configuration requise</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Activité récente</v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item
                v-for="activity in recentActivities"
                :key="activity.id"
              >
                <template v-slot:prepend>
                  <v-icon :color="activity.color">{{ activity.icon }}</v-icon>
                </template>
                <v-list-item-title>{{ activity.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(activity.date) }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { useSnackbarStore } from '@/stores/snackbar'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

const snackbarStore = useSnackbarStore()

const stats = ref({
  totalUsers: 45,
  activeJobs: 8,
  totalApplications: 127,
  systemStatus: 'online'
})

const recentActivities = ref([
  {
    id: 1,
    title: 'Nouveau poste créé',
    date: new Date(),
    icon: 'mdi-briefcase-plus',
    color: 'primary'
  },
  {
    id: 2,
    title: 'Sauvegarde automatique',
    date: new Date(Date.now() - 2 * 60 * 60 * 1000),
    icon: 'mdi-backup-restore',
    color: 'success'
  },
  {
    id: 3,
    title: 'Mise à jour système',
    date: new Date(Date.now() - 24 * 60 * 60 * 1000),
    icon: 'mdi-update',
    color: 'info'
  }
])

const formatDate = (date) => {
  return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: fr })
}

const backupSystem = () => {
  snackbarStore.info('Sauvegarde du système en cours...')
  setTimeout(() => {
    snackbarStore.success('Sauvegarde terminée avec succès')
  }, 2000)
}
</script>
